import { ArrowRight, CrossUI, SpikeCirclePercentage } from 'phx-core';
import { filter } from 'rxjs/operators';

import { Component, EventEmitter, OnInit, Input, Output } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { NzMessageService } from 'ng-zorro-antd';
import { CTA_ACTION_TYPES, EXPANSION_UPSELL_TYPES, ExpansionUpsellService } from '../../service';
import { FeatureUpgradeService } from '@misc/upsell/upgrade.service';
import { Session } from '@services/session';
import { isDesktopView } from '@lib/screen-sizes';

import { IServerUpsellConfig, IUpsellCTA, IUpsellInfo } from '../../interface';

type UpsellType = keyof typeof EXPANSION_UPSELL_TYPES;

import 'style-loader!./sidebar-upsell.component.deep.scss';

@Component({
  selector: 'app-expansion-upsells-sidebar-upsell',
  templateUrl: './sidebar-upsell.component.html',
  styleUrls: ['./sidebar-upsell.component.scss'],
})
export class ExpansionUpsellsSidebarUpsellComponent implements OnInit {
  @Input() public isUpsellVisible: boolean = false;
  @Output() public isUpsellVisibleChange = new EventEmitter<boolean>();

  @Input() public isSideBarMaximized: boolean = false;
  @Input() public isAdminSidebar: boolean = false;

  public readonly icon: string = SpikeCirclePercentage;
  public readonly crossIcon: string = CrossUI;
  public readonly rightArrowIcon: string = ArrowRight;

  public isIconClicked: boolean = false;
  public showModal: boolean = false;

  public upsellConfig: IServerUpsellConfig;
  public upsellType: UpsellType = EXPANSION_UPSELL_TYPES.SIDEBAR as UpsellType;
  public upsellId: string;

  public cachedUpsellConfig: IServerUpsellConfig;
  public cachedCrossSellConfig: IServerUpsellConfig;

  constructor(
    private service: ExpansionUpsellService,
    private featureUpgradeService: FeatureUpgradeService,
    private message: NzMessageService,
    private session: Session,
    private router: Router,
  ) { }

public ngOnInit(): void {
  if (this.session.isDriver() || !this.service.isFeatureFlagEnabled() || !isDesktopView()) return;

  this.router.events
    .pipe(filter(event => event instanceof NavigationEnd))
    .subscribe(() => {
      this.tryShowUpsell(this.router.url);
    });

  this.service.getUpsellInfo().subscribe((info: IUpsellInfo) => {
    this.cachedUpsellConfig = info.upsells.find(
      (upsell) => upsell.type === EXPANSION_UPSELL_TYPES.SIDEBAR
    );

    const currentUrl = this.router.url;
    this.tryShowUpsell(currentUrl);
  });
}

private tryShowUpsell(url: string): void {
  const crossSell = this.service.getDynamicConfig().upsells.find(
    (upsell) =>
      upsell.type === EXPANSION_UPSELL_TYPES.SIDEBAR &&
      upsell['id'] === 'free_hardware_promotion',
  ) as any;

  if (crossSell) {
    this.cachedCrossSellConfig = {
      id: crossSell.id,
      type: crossSell.placements[0].type,
      header: crossSell.placements[0].header[this.service.getUserLanguage()],
      body: crossSell.placements[0].body[this.service.getUserLanguage()],
      backgroundImage: crossSell.placements[0].background_image,
      minimizedImage: crossSell.placements[0].minimized_image,
      modalConfig: {
        heading: crossSell.placements[0].modal_config.heading[this.service.getUserLanguage()],
        description: crossSell.placements[0].modal_config.description[this.service.getUserLanguage()],
        image: crossSell.placements[0].modal_config.image,
        cta: crossSell.placements[0].modal_config.cta.map((cta) => ({
          text: cta.text[this.service.getUserLanguage()],
          action: cta.action,
        })),
      },
    };
  }

  if (crossSell && url.includes(crossSell['display_route']) && this.service.shouldShowFVCrossSell() && this.service.shouldShowSidebarUpsell(crossSell.id)) {
    this.upsellConfig = this.cachedCrossSellConfig;
    this.launchUpsell();
  } else if (this.cachedUpsellConfig) {
    this.upsellConfig = this.cachedUpsellConfig;
    this.launchUpsell();
  } else if (this.cachedCrossSellConfig && this.service.shouldShowFVCrossSell()) {
    this.upsellConfig = this.cachedCrossSellConfig;
    this.launchUpsell();
  } else {
    this.isUpsellVisible = false;
    this.isUpsellVisibleChange.emit(false);
  }
}

  private launchUpsell(): void {
    this.upsellId = this.upsellConfig.id;
    setTimeout(() => {
      if (this.service.shouldShowSidebarUpsell(this.upsellId)) {
        this.service.trackEvent(this.upsellType, this.upsellId, null, 'Show Sidebar Upsell');
        this.isUpsellVisible = true;
        this.isUpsellVisibleChange.emit(true);
        this.service.markSidebarUpsellAsShown(this.upsellId);
      }
    }, 0);
  }

  public showUpsellModal(): void {
    this.showModal = true;
    this.service.trackEvent(this.upsellType, this.upsellId, null, 'Show Upsell Modal');
  }

  public _dismiss(): void {
    this.isUpsellVisible = false;
    this.showModal = false;
    this.isIconClicked = false;
    this.isUpsellVisibleChange.emit(false);
    this.service.markSidebarUpsellAsTemporarilyDismissed(this.upsellId);
  }

  public dismiss(): void {
    this._dismiss();
    this.service.markSidebarUpsellAsDismissed(this.upsellId);
    this.service.trackEvent(this.upsellType, this.upsellId, null, 'Dismiss');
  }

  public dismissModal(): void {
    this._dismiss();
    this.service.markSidebarUpsellAsDismissed(this.upsellId);
    this.showModal = false;
  }

  public togglePopover(): void {
    this.isIconClicked = !this.isIconClicked;
  }

  public handleCTAClick(cta: IUpsellCTA): void {
    if (cta.action.type === CTA_ACTION_TYPES.generateLead) {
      this.featureUpgradeService.genericUpsellWithCustomProps(
        cta.action.campaign_name,
        cta.text as string,
        cta.action.offer_type,
        cta.action.utm_content,
        cta.action.product_interest,
      ).subscribe(() => {
        this._dismiss();
        this.service.markSidebarUpsellAsCompleted(this.upsellId);
        this.service.trackEvent(this.upsellType, this.upsellId, null, cta);
        this.message.success($localize`Thank you for your interest. We will reach out to you shortly.`);
      });
    }
    if (cta.action.type === CTA_ACTION_TYPES.learnMore) {
      this.featureUpgradeService.genericUpsellWithCustomProps(
        cta.action.campaign_name,
        cta.text as string,
        cta.action.offer_type,
        cta.action.utm_content,
        cta.action.product_interest,
      ).subscribe(() => {
        this.service.trackEvent(this.upsellType, this.upsellId, null, cta);
        this._dismiss();
        window.open(cta.action.route, '_blank');
      });
    }
    if (cta.action.type === CTA_ACTION_TYPES.redirect) {
      this._dismiss();
      window.open(cta.action.route, '_blank');
    }
  }

  public getDynamicStyle(): { [key: string]: string } {
    const baseGradient = this.isAdminSidebar
      ? `linear-gradient(256deg, rgba(0, 0, 0, 0.00) 5.49%, rgba(0, 0, 0, 0.80) 96.75%), linear-gradient(0deg, rgba(0, 0, 0, 0.70) 0%, rgba(0, 0, 0, 0.70) 100%)`
      : `linear-gradient(69deg, rgba(0, 0, 0, 0.00) 17.56%, rgba(0, 0, 0, 0.40) 98.83%), linear-gradient(286deg, rgba(0, 0, 0, 0.00) -20.2%, rgba(25, 39, 50, 0.80) 89.15%), linear-gradient(0deg, rgba(25, 39, 50, 0.80) 0%, rgba(25, 39, 50, 0.80) 100%)`;

    return {
      backgroundImage: `${baseGradient}, url('${this.upsellConfig.backgroundImage}')`,
      backgroundPosition: 'center',
      backgroundSize: 'contain',
    };
  }
}